// top-handlers-table.tsx — Top ticket handlers DataTable.
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { TicketHandler } from '@/api/tickets-statistics';

const COLUMNS: ColumnDef<TicketHandler, unknown>[] = [
  {
    id: 'employee_name',
    accessorKey: 'employee_name',
    header: 'Handler',
    cell: ({ row }) => (
      <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
        {row.original.employee_name}
      </span>
    ),
  },
  {
    id: 'total_tickets',
    accessorKey: 'total_tickets',
    header: 'Total',
    cell: ({ row }) => row.original.total_tickets.toLocaleString('en-US'),
  },
  {
    id: 'resolved_tickets',
    accessorKey: 'resolved_tickets',
    header: 'Resolved',
    cell: ({ row }) => row.original.resolved_tickets.toLocaleString('en-US'),
  },
  {
    id: 'resolution_rate',
    header: 'Resolution Rate',
    accessorFn: (row) =>
      row.total_tickets > 0
        ? Math.round((row.resolved_tickets / row.total_tickets) * 100)
        : 0,
    cell: ({ row }) => {
      const rate =
        row.original.total_tickets > 0
          ? Math.round((row.original.resolved_tickets / row.original.total_tickets) * 100)
          : 0;
      return `${rate}%`;
    },
  },
];

interface Props {
  data: TicketHandler[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function TopHandlersTable({ data, isLoading, isError }: Props) {
  const rows = data ?? [];

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Top Ticket Handlers</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load handler data" />
        ) : isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 36 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={COLUMNS}
            data={rows}
            getRowId={(r) => String(r.employee_id)}
            emptyState={
              <EmptyState
                title="No handler data"
                description="No handlers found in this date range."
              />
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
